import type { LucideIcon } from "lucide-react";
import { AlertCircle, RotateCw, Search, X } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// The v3.2 look (--v3-* tokens) in the v3.2 app shell; the classic list
// (Templates without the redesign flag) keeps the older tokens. The flag is
// fixed at build time.
const v3 = (classic: string, redesign: string): string =>
  AppConfig.enableAppRedesign === true ? redesign : classic;

// Shared pieces for the app redesign (VITE_APP_REDESIGN_ENABLED): the same
// column, type scale, cards and pills as the visit workspace, so Visits,
// Templates and Settings read as one product.

export const PAGE_COLUMN =
  "mx-auto w-full max-w-[960px] px-4 pb-6 pt-4 sm:px-6 sm:pt-6 lg:px-8";
export const SECTION_TITLE = v3(
  "text-[17px] font-semibold leading-[22px]",
  "font-v3 text-[17px] font-semibold leading-[22px] text-v3-text",
);
export const GROUP_LABEL = v3(
  "mb-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground",
  "mb-2 font-v3 text-[12px] font-medium uppercase tracking-wide text-v3-text-3",
);
// Edge to edge on phones, a rounded card from 640px (like the note card).
export const LIST_CARD = v3(
  "overflow-hidden border-y bg-card max-sm:-mx-4 sm:rounded-xl sm:border-x",
  "overflow-hidden border-y border-v3-border bg-v3-panel font-v3 text-v3-text max-sm:-mx-4 sm:rounded-v3-panel sm:border-0 sm:shadow-v3-hairline-strong [&_.divide-y>*]:border-v3-border",
);
export const ICON_TILE = v3(
  "flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-5",
  "flex size-10 shrink-0 items-center justify-center rounded-xl bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline [&_svg]:size-5",
);
// Pills, as the workspace's document tabs. Works with aria-pressed (filters)
// and data-state (Radix tabs).
export const CHIP = v3(
  "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring aria-pressed:border-primary aria-pressed:bg-primary/10 aria-pressed:text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-foreground lg:h-9",
  // As the v3.2 filter chip (V3Chip): hairline, ink when pressed or active.
  "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-4 font-v3 text-[13.5px] font-medium text-v3-text-2 shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand aria-pressed:bg-v3-ink aria-pressed:text-v3-on-ink aria-pressed:shadow-none data-[state=active]:bg-v3-ink data-[state=active]:text-v3-on-ink data-[state=active]:shadow-none lg:h-9",
);
// A horizontally scrolling pill row that bleeds to the screen edge on phones.
export const CHIP_ROW =
  "-mx-4 flex min-w-0 gap-2 overflow-x-auto px-4 pb-1 scrollbar-none sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 lg:pb-0";
export const TOUCH_BUTTON = "h-11 lg:h-9";
export const TOUCH_ICON_BUTTON = "size-11 lg:size-9";

// The page's heading. On redesigned pages the top bar shows the title, a
// count and the primary action (Layout's `pageHeader`), so the h1 here is
// for screen readers only; `summary` is an optional visible line under the
// top bar (e.g. the plan in Settings).
export const PageHeader = ({
  title,
  summary,
}: {
  title: React.ReactNode;
  summary?: React.ReactNode;
}): JSX.Element => (
  <header className={summary ? undefined : "sr-only"}>
    <h1 className="sr-only">{title}</h1>
    {summary && (
      <div
        className={v3(
          "text-sm text-muted-foreground",
          "font-v3 text-[13.5px] text-v3-text-3",
        )}
      >
        {summary}
      </div>
    )}
  </header>
);

// The primary action in the top bar: a short label on phones ("New visit")
// and the full one from 640px, 44px tall on touch screens.
export const HeaderActionLabel = ({
  short,
  full,
}: {
  short: React.ReactNode;
  full: React.ReactNode;
}): JSX.Element => (
  <>
    <span className="sm:hidden">{short}</span>
    <span className="max-sm:hidden">{full}</span>
  </>
);

export const SearchField = ({
  label,
  clearLabel,
  value,
  onChange,
  className,
  testId,
}: {
  label: string;
  clearLabel: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  testId?: string;
}): JSX.Element => (
  <div className={cn("relative", className)}>
    <Search
      aria-hidden
      className={v3(
        "pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground",
        "pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-v3-text-3",
      )}
    />
    <input
      aria-label={label}
      className={v3(
        "h-11 w-full rounded-full border bg-card pl-10 pr-11 text-base placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring lg:h-9 lg:text-sm [&::-webkit-search-cancel-button]:hidden",
        "h-11 w-full rounded-full border-0 bg-v3-surface-2 pl-10 pr-11 font-v3 text-base text-v3-text shadow-v3-hairline placeholder:text-v3-text-3 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:h-9 lg:text-sm [&::-webkit-search-cancel-button]:hidden",
      )}
      data-testid={testId}
      placeholder={label}
      type="search"
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
    {value && (
      <button
        aria-label={clearLabel}
        className="absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring lg:size-9"
        type="button"
        onClick={() => {
          onChange("");
        }}
      >
        <X aria-hidden className="size-4" />
      </button>
    )}
  </div>
);

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  children,
  className,
  testId,
}: {
  icon: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  testId?: string;
}): JSX.Element => (
  <section
    className={cn("mx-auto max-w-md py-12 text-center", className)}
    data-testid={testId}
  >
    <span
      className={v3(
        "mx-auto flex size-12 items-center justify-center rounded-2xl bg-primary/10",
        "mx-auto flex size-12 items-center justify-center rounded-2xl bg-v3-surface-2 shadow-v3-hairline",
      )}
    >
      <Icon
        aria-hidden
        className={v3("size-6 text-primary", "size-6 text-v3-text-2")}
      />
    </span>
    <h2 className={cn(SECTION_TITLE, "mt-4")}>{title}</h2>
    {description && (
      <p
        className={v3(
          "mt-1 text-sm text-muted-foreground",
          "mt-1 font-v3 text-[14px] leading-5 text-v3-text-3",
        )}
      >
        {description}
      </p>
    )}
    {children && (
      <div className="mt-5 flex flex-col items-stretch justify-center gap-2 sm:flex-row sm:items-center">
        {children}
      </div>
    )}
  </section>
);

export const LoadErrorBanner = ({
  title,
  description,
  onRetry,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  onRetry?: () => void;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        v3(
          "flex flex-col gap-3 rounded-xl border border-error/30 bg-error/5 p-4 sm:flex-row sm:items-center",
          "flex flex-col gap-3 rounded-xl bg-v3-rec-tint p-4 font-v3 text-v3-text sm:flex-row sm:items-center",
        ),
        className,
      )}
      role="alert"
    >
      <AlertCircle
        aria-hidden
        className={v3(
          "size-5 shrink-0 text-error",
          "size-5 shrink-0 text-v3-rec-text",
        )}
      />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold">{title}</p>
        {description && (
          <p className={v3("text-muted-foreground", "text-v3-text-2")}>
            {description}
          </p>
        )}
      </div>
      {onRetry && (
        <Button
          className={cn(TOUCH_BUTTON, "gap-1.5")}
          type="button"
          variant="outline"
          onClick={onRetry}
        >
          <RotateCw aria-hidden />
          {t("common.retry")}
        </Button>
      )}
    </div>
  );
};
